import React, { useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Field, Input, Textarea, Modal, Spinner, Badge } from '../ui.jsx';

// One-shot wizard: owner pastes their website URL OR 2-3 sample emails →
// Claude extracts brand voice → applied to the account.ai_voice_persona +
// ai_agent_opening. The next time a voice agent re-syncs to ElevenLabs (any
// inbound call), it picks up the new voice automatically.
//
// Designed to be < 60 seconds: paste URL → click Extract → see preview →
// click Apply → done.

export function BrandVoiceWizard({ open, onClose, onApplied, toast }) {
  const [mode, setMode] = useState('url'); // 'url' | 'paste'
  const [url, setUrl] = useState('');
  const [sampleText, setSampleText] = useState('');
  const [busy, setBusy] = useState(false);
  const [extracted, setExtracted] = useState(null);

  if (!open) return null;

  const extract = async () => {
    if (mode === 'url' && !url.trim()) return toast?.('Paste your business website URL first', 'warn');
    if (mode === 'paste' && sampleText.trim().length < 100) return toast?.('Paste at least 100 characters of your real writing (2-3 customer emails work great)', 'warn');
    setBusy(true);
    try {
      const r = await api('/api/onboarding/extract-brand-voice', {
        method: 'POST',
        body: {
          url: mode === 'url' ? url.trim() : null,
          sampleText: mode === 'paste' ? sampleText.trim() : null,
          apply: false, // preview first
        },
      });
      setExtracted(r.extracted);
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      await api('/api/onboarding/extract-brand-voice', {
        method: 'POST',
        body: {
          url: mode === 'url' ? url.trim() : null,
          sampleText: mode === 'paste' ? sampleText.trim() : null,
          apply: true,
        },
      });
      toast?.('✓ Brand voice applied — your AI agent now talks like you do');
      onApplied?.();
      onClose();
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return <Modal open onClose={onClose} width={680}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
      <span style={{ fontSize:22 }}>🎙️</span>
      <h2 style={{ margin:0, fontSize:20 }}>Teach your AI agent how you talk</h2>
    </div>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:18 }}>
      Drop your website URL or paste a few real customer emails. Claude reads them and writes a voice persona your AI uses on calls + texts + emails. Takes about 30 seconds.
    </div>

    {!extracted && <>
      <div style={{ display:'flex', gap:4, marginBottom:14, padding:4, background:G.input, borderRadius:8, width:'fit-content' }}>
        <Btn sz="sm" v={mode==='url'?'primary':'ghost'} onClick={()=>setMode('url')}>🌐 Website URL</Btn>
        <Btn sz="sm" v={mode==='paste'?'primary':'ghost'} onClick={()=>setMode('paste')}>📋 Paste samples</Btn>
      </div>

      {mode === 'url' && <Field label="Your business website URL">
        <Input value={url} onChange={e=>setUrl(e.target.value)} placeholder="https://mrelectricmedic.com" autoFocus/>
      </Field>}

      {mode === 'paste' && <Field label="Paste 2-3 customer emails / SMS replies / About Us text">
        <Textarea value={sampleText} onChange={e=>setSampleText(e.target.value)} placeholder="Paste your actual writing — customer follow-up emails work best. Even 1 long email captures most of your voice." style={{ minHeight:180 }}/>
      </Field>}

      <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
        <Btn onClick={onClose}>Skip for now</Btn>
        <Btn v="primary" onClick={extract} disabled={busy}>{busy ? <Spinner sz={14}/> : '🪄 Extract voice'}</Btn>
      </div>
    </>}

    {extracted && <>
      <div style={{ ...styles.card, padding:16, marginBottom:14, borderColor:G.purple+'66' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
          <Badge c={G.purple} bg={G.purpleDim}>Tone</Badge>
          <strong style={{ fontSize:14 }}>{extracted.tone}</strong>
          <span style={{ fontSize:12, color:G.textMuted }}>· uses "{extracted.pronoun}" · {extracted.sentence_length} sentences</span>
        </div>
        <div style={{ fontSize:13, lineHeight:1.6, marginBottom:10 }}>{extracted.persona}</div>

        <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>Signature phrases</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
          {(extracted.signature_phrases || []).map((p, i) => (
            <span key={i} style={{ fontSize:12, padding:'3px 10px', background:G.input, borderRadius:12 }}>"{p}"</span>
          ))}
        </div>

        <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>Opening line your AI will use</div>
        <div style={{ ...styles.card, padding:'10px 14px', background:G.input, fontSize:13, fontStyle:'italic' }}>
          "{extracted.opening_line}"
        </div>
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10 }}>
        <Btn onClick={()=>setExtracted(null)}>← Try again</Btn>
        <div style={{ display:'flex', gap:10 }}>
          <Btn onClick={onClose}>Skip</Btn>
          <Btn v="primary" onClick={apply} disabled={busy}>{busy ? <Spinner sz={14}/> : '✓ Use this voice'}</Btn>
        </div>
      </div>
    </>}
  </Modal>;
}
